import React, { useState, useEffect } from 'react';
import {
  Modal,
  Table,
  Button,
  Space,
  Select,
  Input,
  InputNumber,
  DatePicker,
  Alert,
  Typography,
  App,
  Steps,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import {
  useEligibleWorkOrders,
  useCreateDeliveryFromWorkOrder,
} from '@/hooks/useDelivery';
import type {
  WorkOrderReadyForDelivery,
  WorkOrderReadyLineItem,
  CreateDeliveryFromWorkOrderPayload,
} from '@/services/delivery.service';
import { resolveDeliveryExecutionMode } from '@/utils/executionScope';

const { TextArea } = Input;
const { Option } = Select;

interface CreateDeliveryFromWorkOrderModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (deliveryId: string, deliveryNumber: string) => void;
}

export const CreateDeliveryFromWorkOrderModal: React.FC<CreateDeliveryFromWorkOrderModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { message } = App.useApp();
  const [step, setStep] = useState(0);
  const [customerIdFilter, setCustomerIdFilter] = useState<string | undefined>();
  const [selectedWO, setSelectedWO] = useState<WorkOrderReadyForDelivery | null>(null);
  const [lineQuantities, setLineQuantities] = useState<Record<string, number>>({});
  /** When a WO line is unit-tracked, selected `production_unit_number` for this delivery. */
  const [lineUnitByLineId, setLineUnitByLineId] = useState<Record<string, number>>({});
  const [deliveryDate, setDeliveryDate] = useState(dayjs());
  const [customerPo, setCustomerPo] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [notes, setNotes] = useState('');

  const { data: eligibleList = [], isLoading: loadingWOs } = useEligibleWorkOrders(customerIdFilter);
  const createMutation = useCreateDeliveryFromWorkOrder();

  useEffect(() => {
    if (!open) {
      setStep(0);
      setSelectedWO(null);
      setLineQuantities({});
      setLineUnitByLineId({});
      setDeliveryDate(dayjs());
      setCustomerPo('');
      setDeliveryAddress('');
      setNotes('');
    }
  }, [open]);

  const deliveryMode = selectedWO
    ? resolveDeliveryExecutionMode({
        execution_scope: selectedWO.execution_scope,
        operation_generation_mode: selectedWO.delivery_execution_mode,
      })
    : 'boq_line';
  const isUnitWiseDelivery = deliveryMode === 'unit_wise';

  useEffect(() => {
    if (selectedWO && step === 1) {
      const qtyInit: Record<string, number> = {};
      const unitInit: Record<string, number> = {};
      const mode = resolveDeliveryExecutionMode({
        execution_scope: selectedWO.execution_scope,
        operation_generation_mode: selectedWO.delivery_execution_mode,
      });
      selectedWO.line_items.forEach((li) => {
        const units = li.completed_production_units ?? [];
        if (mode === 'unit_wise' && units.length > 0) {
          qtyInit[li.id] = Math.min(1, li.pending);
          unitInit[li.id] = units[0];
        } else {
          qtyInit[li.id] = li.pending;
        }
      });
      setLineQuantities(qtyInit);
      setLineUnitByLineId(unitInit);
    }
  }, [selectedWO, step]);

  const handleSelectWO = (wo: WorkOrderReadyForDelivery) => {
    setSelectedWO(wo);
    setStep(1);
  };

  const handleBack = () => {
    if (step === 1) {
      setSelectedWO(null);
      setStep(0);
    }
  };

  const handleSubmit = async () => {
    if (!selectedWO) return;
    const items = selectedWO.line_items
      .map((li) => {
        const units = li.completed_production_units ?? [];
        const qty = lineQuantities[li.id] ?? 0;
        const pin =
          isUnitWiseDelivery && units.length > 0 ? lineUnitByLineId[li.id] : undefined;
        return {
          work_order_line_item_id: li.id,
          delivered_quantity: qty,
          ...(pin != null ? { production_unit_number: pin } : {}),
        };
      })
      .filter((i) => i.delivered_quantity > 0);
    if (items.length === 0) {
      message.warning('Enter at least one line with quantity > 0');
      return;
    }
    for (const li of selectedWO.line_items) {
      const units = li.completed_production_units ?? [];
      const qty = lineQuantities[li.id] ?? 0;
      if (qty <= 0) continue;
      if (isUnitWiseDelivery && units.length > 0 && lineUnitByLineId[li.id] == null) {
        message.warning(`Select a WO unit for line ${li.line_number}`);
        return;
      }
    }
    const payload: CreateDeliveryFromWorkOrderPayload = {
      work_order_id: selectedWO.id,
      delivery_date: deliveryDate.format('YYYY-MM-DD'),
      customer_po_number: customerPo || undefined,
      delivery_address: deliveryAddress || undefined,
      notes: notes || undefined,
      items,
    };
    try {
      const delivery = await createMutation.mutateAsync(payload);
      onSuccess?.(delivery.id, delivery.delivery_note_number);
      onClose();
    } catch {
      // error already shown in mutation
    }
  };

  const woColumns: ColumnsType<WorkOrderReadyForDelivery> = [
    { title: 'Work Order', dataIndex: 'work_order_number', key: 'wo_number', width: 130 },
    { title: 'Customer Order', dataIndex: 'sales_order_number', key: 'so_number', width: 130 },
    { title: 'Customer', dataIndex: 'customer_name', key: 'customer_name', ellipsis: true },
    { title: 'Status', dataIndex: 'work_order_status', key: 'status', width: 110 },
    {
      title: 'Pending',
      key: 'pending_wo',
      width: 90,
      render: (_: unknown, r: WorkOrderReadyForDelivery) => `${r.pending_wo} ${r.line_items[0]?.uom ?? ''}`,
    },
    {
      title: 'Action',
      key: 'action',
      width: 100,
      render: (_: unknown, r: WorkOrderReadyForDelivery) => (
        <Button type="primary" size="small" onClick={() => handleSelectWO(r)}>
          Select
        </Button>
      ),
    },
  ];

  const totalQty = selectedWO
    ? selectedWO.line_items.reduce((sum, li) => sum + (lineQuantities[li.id] ?? 0), 0)
    : 0;

  return (
    <Modal
      title="Create Delivery Note"
      open={open}
      onCancel={onClose}
      footer={null}
      width={step === 0 ? 800 : 900}
      destroyOnClose
    >
      <Steps current={step} size="small" style={{ marginBottom: 16 }}>
        <Steps.Step title="Select Work Order" />
        <Steps.Step title="Quantities & Details" />
      </Steps>

      {step === 0 && (
        <>
          <Space style={{ marginBottom: 16 }} wrap>
            <Typography.Text>Customer (optional):</Typography.Text>
            <Select
              placeholder="All customers"
              allowClear
              style={{ width: 220 }}
              value={customerIdFilter}
              onChange={setCustomerIdFilter}
            >
              {Array.from(
                new Map(eligibleList.map((wo) => [wo.customer_id, wo.customer_name])).entries()
              ).map(([id, name]) => (
                <Option key={id} value={id}>
                  {name || id}
                </Option>
              ))}
            </Select>
          </Space>
          {eligibleList.length === 0 && !loadingWOs && (
            <Alert
              type="info"
              message="No Work Orders ready for delivery"
              description="Ensure each shipped line has an approved (or partial) Final Inspection Request, and create the delivery note from this work order. An Inspection Release Note (IRN) is created and approved on the delivery note before marking in transit."
              style={{ marginBottom: 16 }}
            />
          )}
          <Table
            rowKey="id"
            columns={woColumns}
            dataSource={eligibleList}
            loading={loadingWOs}
            pagination={{ pageSize: 5 }}
            size="small"
          />
          {step === 0 && (
            <div style={{ marginTop: 16, textAlign: 'right' }}>
              <Button onClick={onClose}>Cancel</Button>
            </div>
          )}
        </>
      )}

      {step === 1 && selectedWO && (
        <>
          <Alert
            type="info"
            message={`Work Order ${selectedWO.work_order_number} – Customer Order ${selectedWO.sales_order_number}`}
            description={`Customer: ${selectedWO.customer_name ?? '-'} | Pending (this WO): ${selectedWO.pending_wo} — ${
              isUnitWiseDelivery
                ? 'Fabrication: ship by completed unit (partial allowed).'
                : 'Non-Fabrication: ship full completed quantity per line only (no partial delivery).'
            }`}
            style={{ marginBottom: 16 }}
          />
          <Table
            rowKey="id"
            dataSource={selectedWO.line_items}
            size="small"
            pagination={false}
            columns={[
              { title: '#', dataIndex: 'line_number', key: 'line_number', width: 50 },
              { title: 'Description', dataIndex: 'item_description', key: 'desc', ellipsis: true },
              {
                title: 'Revision',
                dataIndex: 'revision',
                key: 'revision',
                width: 72,
                render: (v: string) => v || '—',
              },
              {
                title: 'Mfg complete (max)',
                key: 'mfg',
                width: 120,
                render: (_: unknown, r: WorkOrderReadyLineItem) =>
                  `${r.manufacturing_deliverable_quantity ?? '—'} ${r.uom}`,
              },
              {
                title: 'Pending',
                key: 'pending',
                width: 80,
                render: (_: unknown, r: WorkOrderReadyLineItem) => `${r.pending} ${r.uom}`,
              },
              ...(isUnitWiseDelivery
                ? [{
                title: 'WO unit',
                key: 'wo_unit',
                width: 110,
                render: (_: unknown, r: WorkOrderReadyLineItem) => {
                  const units = r.completed_production_units ?? [];
                  if (units.length === 0) return '—';
                  return (
                    <Select
                      size="small"
                      style={{ width: '100%' }}
                      value={lineUnitByLineId[r.id]}
                      onChange={(v) => setLineUnitByLineId((prev) => ({ ...prev, [r.id]: Number(v) }))}
                    >
                      {units.map((u) => (
                        <Option key={u} value={u}>
                          {u}
                        </Option>
                      ))}
                    </Select>
                  );
                },
              }]
                : []),
              {
                title: 'This delivery',
                key: 'qty',
                width: 120,
                render: (_: unknown, r: WorkOrderReadyLineItem) => {
                  const hasUnits =
                    isUnitWiseDelivery && (r.completed_production_units?.length ?? 0) > 0;
                  const maxQty = hasUnits ? Math.min(1, r.pending) : r.pending;
                  const readOnlyFullLine = !isUnitWiseDelivery && r.pending > 0;
                  return (
                    <InputNumber
                      min={0}
                      max={maxQty}
                      disabled={readOnlyFullLine}
                      value={lineQuantities[r.id] ?? (hasUnits ? Math.min(1, r.pending) : r.pending)}
                      onChange={(v) => setLineQuantities((prev) => ({ ...prev, [r.id]: Number(v) ?? 0 }))}
                      style={{ width: '100%' }}
                    />
                  );
                },
              },
            ]}
          />
          <Space direction="vertical" style={{ marginTop: 16, width: '100%' }} size="small">
            <Space wrap>
              <Typography.Text>Delivery date:</Typography.Text>
              <DatePicker value={deliveryDate} onChange={(d) => d && setDeliveryDate(d)} />
              <Typography.Text>L.P.O:</Typography.Text>
              <Input placeholder="Customer PO" value={customerPo} onChange={(e) => setCustomerPo(e.target.value)} style={{ width: 160 }} />
            </Space>
            <div>
              <Typography.Text>Delivery address:</Typography.Text>
              <Input.TextArea
                placeholder="Address"
                value={deliveryAddress}
                onChange={(e) => setDeliveryAddress(e.target.value)}
                rows={2}
                style={{ marginTop: 4 }}
              />
            </div>
            <div>
              <Typography.Text>Notes:</Typography.Text>
              <Input.TextArea placeholder="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={1} style={{ marginTop: 4 }} />
            </div>
            <Typography.Text type="secondary">Total quantity this delivery: {totalQty}</Typography.Text>
          </Space>
          <div style={{ marginTop: 16, textAlign: 'right' }}>
            <Space>
              <Button onClick={handleBack}>Back</Button>
              <Button onClick={onClose}>Cancel</Button>
              <Button
                type="primary"
                onClick={handleSubmit}
                loading={createMutation.isPending}
                disabled={totalQty <= 0}
              >
                Create Delivery Note
              </Button>
            </Space>
          </div>
        </>
      )}
    </Modal>
  );
};

export default CreateDeliveryFromWorkOrderModal;
